import { toBlob, getFontEmbedCSS } from 'html-to-image';

// Turns a rendered DOM node into a PNG in the browser (html-to-image draws the
// node into an SVG <foreignObject> and rasterises it on a canvas), plus the
// download step. No server round-trip: the availability images are generated
// from data the admin's tab already holds.

/**
 * Resolves once the browser has laid out and painted the current DOM.
 * Two frames because the first only guarantees style/layout has been
 * scheduled; the node must be fully laid out before it is cloned.
 * @returns {Promise<void>}
 */
export const waitForLayout = async () => {
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // A font that fails to load just falls back; never worth aborting over.
    }
  }
  await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
};

/**
 * Waits for every <img> inside a node to finish decoding. An image still
 * loading when the node is cloned comes out blank in the PNG.
 * @param {HTMLElement} node
 * @returns {Promise<void>}
 */
const waitForImages = async (node) => {
  const images = Array.from(node.querySelectorAll('img'));
  await Promise.all(images.map(img => (img.decode ? img.decode().catch(() => null) : null)));
};

/**
 * The @font-face CSS for the web fonts a node uses, inlined as data URLs.
 *
 * Fetched ONCE and reused for every template — html-to-image would otherwise
 * download the Google Fonts files again for each image. Returns null when the
 * fonts can't be read (offline, blocked); the images then render in the
 * system font rather than failing outright.
 *
 * @param {HTMLElement} node
 * @returns {Promise<string|null>}
 */
export const getEmbeddedFontCss = async (node) => {
  if (!node) return null;
  try {
    return (await getFontEmbedCSS(node)) || null;
  } catch (error) {
    console.error('Availability image: web fonts could not be embedded, using system fonts.', error);
    return null;
  }
};

/**
 * Hands the main thread back to the browser for a moment — long enough for a
 * pending click, keystroke or paint to run — before the next heavy step.
 * Used between images so generating them in the background never freezes the
 * dialog's controls. Uses requestIdleCallback where the browser has it
 * (capped so a busy page can't stall the queue), else a 0ms timeout.
 * @returns {Promise<void>}
 */
export const yieldToBrowser = () => new Promise((resolve) => {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => resolve(), { timeout: 120 });
  } else {
    setTimeout(resolve, 0);
  }
});

/**
 * Renders a node as a PNG Blob at exactly the given size.
 *
 * A Blob rather than a data URL: the canvas encodes it with toBlob(), which
 * the browser does off the main thread, and the result is shown and
 * downloaded through an object URL instead of a multi-megabyte string.
 *
 * @param {HTMLElement} node - the template root, already mounted and laid out
 * @param {object} options
 * @param {number} options.width - output width in px
 * @param {number} options.height - output height in px
 * @param {string|null} [options.fontEmbedCSS] - from getEmbeddedFontCss()
 * @returns {Promise<Blob>} image/png
 * @throws {Error} when the node is missing or the browser refuses to draw it
 */
export const renderElementToBlob = async (node, { width, height, fontEmbedCSS = null }) => {
  if (!node) throw new Error('The image template has not finished loading. Please try again.');
  await waitForImages(node);
  const options = {
    width,
    height,
    canvasWidth: width,
    canvasHeight: height,
    // 1, not devicePixelRatio: the output must be exactly the template's size
    // on every device, not double it on a retina laptop.
    pixelRatio: 1,
  };
  if (fontEmbedCSS) options.fontEmbedCSS = fontEmbedCSS;
  else options.skipFonts = true;
  let blob;
  try {
    blob = await toBlob(node, options);
  } catch (error) {
    throw new Error(`Could not draw the image: ${error?.message || 'unknown browser error'}`);
  }
  if (!blob) throw new Error('Could not draw the image: the browser returned an empty picture.');
  return blob;
};

/**
 * Saves a data: or blob: URL as a file.
 *
 * Goes through a fresh Blob URL rather than putting a data URL on the link
 * directly: some mobile browsers ignore `download` on a multi-megabyte data
 * URL and open it in a new tab instead.
 *
 * @param {string} dataUrl - data: or blob: URL
 * @param {string} fileName
 * @returns {Promise<void>}
 * @throws {Error} when the data URL can't be read back
 */
export const downloadDataUrl = async (dataUrl, fileName) => {
  if (!dataUrl) throw new Error('There is no image to download yet.');
  const blob = await (await fetch(dataUrl)).blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on a delay — revoking straight after click() cancels the
  // download in Safari before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

// File extension per image type, for naming a saved image after what it
// actually is rather than assuming PNG.
const IMAGE_EXTENSIONS = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
};

/**
 * Saves an image the page is already showing — a data URL or a web URL —
 * as a file named `baseName` plus the extension of its real type.
 *
 * Read through fetch() so the browser downloads it instead of navigating to
 * it. A web URL on another host that doesn't allow that read (no CORS
 * headers) throws, and the caller decides what to fall back to.
 *
 * @param {string} url - data: or http(s): URL of the image
 * @param {string} baseName - file name without an extension
 * @returns {Promise<void>}
 * @throws {Error} when there is no URL or the image can't be read
 */
export const downloadImageUrl = async (url, baseName) => {
  if (!url) throw new Error('There is no image to download.');
  const response = await fetch(url);
  if (!response.ok) throw new Error(`The image could not be read (${response.status}).`);
  const blob = await response.blob();
  const extension = IMAGE_EXTENSIONS[blob.type] || 'png';
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${baseName || 'image'}.${extension}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Delayed for the same Safari reason as downloadDataUrl above.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
};
